/*
    © Copyright LinkHMS B.V. All rights reserved.
    * Proprietary and confidential.
    * Unauthorized copying of this file, via any medium is strictly prohibited.
*/
import { registerLocaleData } from '@angular/common';
import { DestroyRef, DOCUMENT, inject, Injectable, ɵfindLocaleData } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SupportedLanguages } from '@common/enums/supported-languages.enum';
import { LANG_DEFAULT, LANG_LIST } from '@core/i18n/language-list';
import { ILang } from '@core/i18n/translates.interface';
import { MetaService } from '@core/meta-data/meta.service';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateService as NGXTranslateService,
} from '@ngx-translate/core';
import { StoreService } from '@shared/services/store.service';
import { BehaviorSubject, forkJoin, from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

const STORAGE_LANG_NAME = 'langCode';

@Injectable()
export class I18nService {
  private _document: Document = inject(DOCUMENT);
  private _destroyRef = inject(DestroyRef);
  private _translate: NGXTranslateService = inject(NGXTranslateService);
  private _meta: MetaService = inject(MetaService);
  private _appStorage: StoreService = inject(StoreService);

  public currentLanguage$ = new BehaviorSubject<ILang>(LANG_DEFAULT);
  public currentLocaleCulture = LANG_DEFAULT.culture;

  constructor() {
    this.currentLanguage$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap((l) => {
          try {
            ɵfindLocaleData(l.culture);
            return forkJoin({ currentConfig: of(l), localeRegistered: of(true), locale: of(null) });
          } catch (e) {
            return forkJoin({
              currentConfig: of(l),
              localeRegistered: of(false),
              locale: this.getAppLocale(l.code),
            });
          }
        }),
      )
      .subscribe((result) => {
        if (!result.localeRegistered) {
          if (!result.locale)
            throw new Error(
              `Cannot find locale inside angular @angular/common/locales/${result.currentConfig.code}`,
            );

          registerLocaleData(result.locale, result.currentConfig.code);
        }
      });
  }

  checkDefaultLocales() {
    const locales = navigator.languages ?? [];
    locales.forEach((locale) => {
      // if already handled by i18 service
      if (LANG_LIST.some((lang) => lang.culture === locale)) return;

      try {
        ɵfindLocaleData(locale);
      } catch (e) {
        console.warn(`Locale ${locale} is not supported. Falling back to 'en'.`);
        from(import(`@angular/common/locales/en`).then((v) => v.default))
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe((config) => {
            // Register default locale for unsupported
            registerLocaleData(config, locale);
          });
      }
    });
  }

  public initLanguage(): Promise<void> {
    return new Promise((resolve) => {
      this._translate.addLangs(LANG_LIST.map((lang: ILang) => lang.code));
      const language: ILang = this._getLanguage();
      if (language) {
        this._translate.setDefaultLang(language.code);
      } else {
        this._translate.setDefaultLang(LANG_DEFAULT.code);
      }
      this._setLanguage(language);
      resolve();
    });
  }

  public changeLang(code: string): void {
    const lang = this._getFindLang(code);
    if (!lang || lang.code === this._translate.currentLang) {
      return;
    }
    this._appStorage.setItem(STORAGE_LANG_NAME, lang.code);
    this._setLanguage(lang);
  }

  public getLangList(): Observable<ILang[]> {
    return of(LANG_LIST);
  }

  public getCurrentLang(): ILang {
    const lang = this._getFindLang(this._translate.currentLang);
    if (!lang) {
      throw new Error('No lang');
    }
    return lang;
  }

  private _getLanguage(): ILang {
    const item = this._appStorage.getItem(STORAGE_LANG_NAME);

    let language = item && this._getFindLang(item);

    if (language) {
      return language;
    }

    const lang = this.getLanguageAccordingToBrowser();

    language = lang || LANG_DEFAULT;
    this._appStorage.setItem(STORAGE_LANG_NAME, language.code);
    return language;
  }

  private getLanguageAccordingToBrowser(): ILang | null {
    const browserLanguageCulture = navigator?.language;
    return LANG_LIST.find((l) => l.code === browserLanguageCulture) ?? null;
  }

  private _getFindLang(code: string): ILang | null {
    return (code && LANG_LIST.find((lang: ILang) => lang.code === code)) || null;
  }

  private _setLanguage(lang: ILang): void {
    this._translate
      .use(lang.code)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._meta.setTag('og:locale', lang.culture);
        this._document.documentElement.lang = lang.code;
        this.currentLocaleCulture = lang.culture;
        this.currentLanguage$.next(lang);
      });
  }

  private getAppLocale(localeCode: SupportedLanguages) {
    switch (localeCode) {
      case SupportedLanguages.en:
        return from(import(`@angular/common/locales/en`).then((v) => v.default));
      case SupportedLanguages.fr:
        return from(import(`@angular/common/locales/fr`).then((v) => v.default));
      case SupportedLanguages.pt:
        return from(import(`@angular/common/locales/pt`).then((v) => v.default));
      case SupportedLanguages.es:
        return from(import(`@angular/common/locales/es`).then((v) => v.default));
    }
  }
}

export class CommonMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams): string {
    const keyRegex = new RegExp(/\w+\.\w+/);
    const translations = params.translateService.translations as Record<
      string,
      Record<string, any>
    >;

    if (keyRegex.exec(params.key) && translations['en'] && !translations['en'][params.key]) {
      console.warn(`Missing translate for "${params.key}"`);
    }
    return params.key;
  }
}
